<template>
  <div class="app-container">
    <Container
      :title="t('route.supplierCreate')"
    >
      <template #main>
        <DynamicRadioForm
          ref="createFormRef"
          :formModel="formModel"
          :formStruct="formStruct"
          :formRules="supplierRules"
          :groupsStruct="groupFormStruct"
          :groupsRules="bankRules"
          :form-width="900"
          :labelWidth="'200px'"
          :groupLabelWidth="'150px'"
          :defaultGroup="true"
          @submit="toSubmit"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import Container from '@/components/form/container/Container.vue'
import DynamicRadioForm from '@/components/form/common/DynamicRadioForm.vue'
import { CreateSupplierModel, SupplierBankModel, SupplierInfoModel } from '@/model/master-data/supplierModel'
import { ElForm, ElMessage } from 'element-plus'
import { createSupplier } from '@/apis/main-data/supplier'
import { useRouter } from 'vue-router'

export default defineComponent({
  components: {
    Container,
    DynamicRadioForm
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const createFormRef = ref(ElForm)
    const options = [
      { label: t('common.yes'), value: true },
      { label: t('common.no'), value: false }
    ]
    const supplierStruct = [
      { prop: 'name', label: t('company.companyName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'accountNumber', label: t('supplier.supplierCode'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isForeignCurrency',
        label: t('bank.isForeignExchangeOrExternal'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: options
      },
      { prop: 'paymentTerm', label: t('bank.paymentTerms'), component: 'el-input', type: 'text', model: '', decimalPlaces: 0, isPositive: true },
      { prop: 'comment', label: t('common.notes'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isDelete',
        label: t('common.deleteStatus'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: options
      },
      { prop: 'vendorBanks', label: t('bank.bankInfo'), component: 'custom-groups' }
    ]
    const supplierRules = {
      name: [{ required: true, message: t('company.companyNameP'), trigger: 'blur' }],
      accountNumber: [{ required: true, message: t('supplier.supplierCodeP'), trigger: 'blur' }],
      paymentTerm: [{ required: true, message: t('bank.paymentTermsP'), trigger: 'blur' }]
    }
    const bankStruct = [
      { prop: 'account', label: t('bank.bankAccount'), component: 'el-input', type: 'text', model: '' },
      { prop: 'code', label: t('bank.bankCode'), component: 'el-input', type: 'text', model: '' },
      { prop: 'name', label: t('bank.bankName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'city', label: t('bank.bankCity'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isCompanyAccount',
        label: t('order.isCompanyAccount'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      {
        prop: 'isPriorityAcceptanceBill',
        label: t('bank.isPriorityAcceptanceBill'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      }
    ]
    const bankRules = {
      account: [{ required: true, message: t('bank.bankAccountP'), trigger: 'blur' }],
      code: [{ required: true, message: t('bank.bankCodeP'), trigger: 'blur' }],
      name: [{ required: true, message: t('bank.bankNameP'), trigger: 'blur' }],
      city: [{ required: true, message: t('bank.bankCityP'), trigger: 'blur' }],
      isCompanyAccount: [{ required: true, message: t('order.isCompanyAccountP'), trigger: 'change' }],
      isPriorityAcceptanceBill: [{ required: true, message: t('bank.isPriorityAcceptanceBillP'), trigger: 'change' }]
    }
    const dataMap = reactive({
      formStruct: supplierStruct,
      formModel: createModel(supplierStruct) as SupplierInfoModel,
      groupFormStruct: bankStruct,
      groupFormModel: createModel(bankStruct) as SupplierBankModel,
      async toSubmit(submitData: any) {
        const params: CreateSupplierModel = {
          accountNumber: submitData.accountNumber,
          isDelete: submitData.isDelete,
          isForeignCurrency: submitData.isForeignCurrency,
          name: submitData.name,
          comment: submitData.comment,
          paymentTerm: submitData.paymentTerm,
          vendorBanks: submitData.model
        }
        const res = await createSupplier(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
          createFormRef.value.resetEmit()
          await router.push({ name: 'SupplierPageList', state: { refresh: true } })
        } else {
          if (res?.code === 4092) {
            ElMessage({
              message: '供应商已经存在',
              type: 'error',
              duration: 2000
            })
          } else {
            ElMessage({
              message: res?.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
      }
    })
    return {
      t,
      createFormRef,
      supplierRules,
      bankRules,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
